import * as THREE from 'three/webgpu'
import type { Viewer } from '../Viewer'
import type { CameraControls } from '../../camera/CameraControls'
import { addObject3DToParent } from '../../utils/scene-object/addObject3DToParent'
import { solidworksDefaultPreset } from '../../imported-model/render/solidworks-default-preset'

/**
 * 光照管理器配置。
 */
export interface LightManagerOptions {
  /**
   * 环境光颜色。
   */
  ambientColor?: THREE.ColorRepresentation
  /**
   * 环境光强度。
   */
  ambientIntensity?: number
  /**
   * 主方向光颜色。
   */
  directionalColor?: THREE.ColorRepresentation
  /**
   * 主方向光强度。
   */
  directionalIntensity?: number
  /**
   * 主方向光位置。
   */
  directionalPosition?: [number, number, number]
  /**
   * 补光颜色。
   */
  fillLightColor?: THREE.ColorRepresentation
  /**
   * 补光强度。
   */
  fillLightIntensity?: number
  /**
   * 补光位置。
   */
  fillLightPosition?: [number, number, number]
  /**
   * 反向补光颜色。
   */
  backLightColor?: THREE.ColorRepresentation
  /**
   * 反向补光强度。
   */
  backLightIntensity?: number
  /**
   * 反向补光位置。
   */
  backLightPosition?: [number, number, number]
  /**
   * 相机同步光组到目标点的距离。
   */
  cameraSynchronizedLightDistance?: number
}

/**
 * 光照管理器。
 *
 * 设计说明：
 * 1. 光照配置由 viewer 统一注入，避免导入件和原生结果体各自维护一套灯光参数；
 * 2. 采用一盏环境光加三盏平行光，对齐 SOLIDWORKS 默认视口的“Ambient + 3 Directional”灯光结构；
 * 3. 平行光按相机坐标系动态同步，避免旋转到模型背面后固定世界光源把可见面压黑；
 * 4. 所有灯光都由本模块集中创建和释放，满足 Web3D 资源生命周期要求。
 */
export class LightManager {
  /**
   * Viewer 实例。
   */
  private readonly viewer: Viewer

  /**
   * 场景引用。
   */
  private readonly scene: THREE.Scene

  /**
   * 环境光实例。
   */
  private readonly ambientLight: THREE.AmbientLight

  /**
   * 主方向光实例。
   */
  private readonly directionalLight: THREE.DirectionalLight

  /**
   * 补光实例。
   */
  private readonly fillLight: THREE.DirectionalLight

  /**
   * 反向补光实例。
   */
  private readonly backLight: THREE.DirectionalLight

  /**
   * 相机同步光组到目标点的距离。
   */
  private readonly cameraSynchronizedLightDistance: number

  /**
   * 相机前向缓存，避免渲染循环分配临时向量。
   */
  private readonly cameraForward = new THREE.Vector3()

  /**
   * 相机右向缓存，避免渲染循环分配临时向量。
   */
  private readonly cameraRight = new THREE.Vector3()

  /**
   * 相机上向缓存，避免渲染循环分配临时向量。
   */
  private readonly cameraUp = new THREE.Vector3()

  /**
   * 动态光组目标点缓存。
   */
  private readonly synchronizedLightTarget = new THREE.Vector3()

  /**
   * 动态光源位置缓存。
   */
  private readonly synchronizedLightPosition = new THREE.Vector3()

  /**
   * 当前正在监听的相机控制器。
   */
  private cameraControls: CameraControls | null = null

  /**
   * CameraControls 变化事件处理器。
   */
  private readonly handleCameraControlsChange = (): void => {
    this.syncWithActiveCamera()
  }

  /**
   * @param viewer Viewer 实例。
   * @param options 光照覆盖配置，未传入时统一使用 SolidWorks 视口默认预设。
   */
  public constructor(viewer: Viewer, options: LightManagerOptions = {}) {
    const defaultLighting = solidworksDefaultPreset.lighting
    const {
      ambientColor = defaultLighting.ambientColor,
      ambientIntensity = defaultLighting.ambientIntensity,
      directionalColor = defaultLighting.directionalColor,
      directionalIntensity = defaultLighting.directionalIntensity,
      directionalPosition = defaultLighting.directionalPosition,
      fillLightColor = defaultLighting.fillLightColor,
      fillLightIntensity = defaultLighting.fillLightIntensity,
      fillLightPosition = defaultLighting.fillLightPosition,
      backLightColor = defaultLighting.backLightColor,
      backLightIntensity = defaultLighting.backLightIntensity,
      backLightPosition = defaultLighting.backLightPosition,
      cameraSynchronizedLightDistance = defaultLighting.cameraSynchronizedLightDistance,
    } = options

    this.viewer = viewer
    this.scene = viewer.scene
    this.cameraSynchronizedLightDistance = Number.isFinite(cameraSynchronizedLightDistance)
      && cameraSynchronizedLightDistance > 0
      ? cameraSynchronizedLightDistance
      : defaultLighting.cameraSynchronizedLightDistance

    this.ambientLight = new THREE.AmbientLight(ambientColor, ambientIntensity)
    addObject3DToParent(this.ambientLight, this.scene)

    this.directionalLight = this.createDirectionalLight(
      directionalColor,
      directionalIntensity,
      directionalPosition,
    )

    this.fillLight = this.createDirectionalLight(
      fillLightColor,
      fillLightIntensity,
      fillLightPosition,
    )

    this.backLight = this.createDirectionalLight(
      backLightColor,
      backLightIntensity,
      backLightPosition,
    )

    this.bindCameraControls(viewer.controls)
  }

  /**
   * 获取环境光。
   *
   * @returns 环境光实例。
   */
  public getAmbientLight(): THREE.AmbientLight {
    return this.ambientLight
  }

  /**
   * 获取主方向光。
   *
   * @returns 主方向光实例。
   */
  public getDirectionalLight(): THREE.DirectionalLight {
    return this.directionalLight
  }

  /**
   * 获取补光。
   *
   * @returns 补光实例。
   */
  public getFillLight(): THREE.DirectionalLight {
    return this.fillLight
  }

  /**
   * 获取反向补光。
   *
   * @returns 反向补光实例。
   */
  public getBackLight(): THREE.DirectionalLight {
    return this.backLight
  }

  /**
   * 获取全部平行光。
   *
   * @returns 当前光照管理器维护的平行光列表。
   */
  public getDirectionalLights(): THREE.DirectionalLight[] {
    return [this.directionalLight, this.fillLight, this.backLight]
  }

  /**
   * 设置环境光强度。
   *
   * @param intensity 强度值。
   */
  public setAmbientIntensity(intensity: number): void {
    this.ambientLight.intensity = intensity
  }

  /**
   * 设置主方向光强度。
   *
   * @param intensity 强度值。
   */
  public setDirectionalIntensity(intensity: number): void {
    this.directionalLight.intensity = intensity
  }

  /**
   * 设置主方向光位置。
   *
   * @param x X 坐标。
   * @param y Y 坐标。
   * @param z Z 坐标。
   */
  public setDirectionalPosition(x: number, y: number, z: number): void {
    this.directionalLight.position.set(x, y, z)
  }

  /**
   * 设置补光强度。
   *
   * @param intensity 强度值。
   */
  public setFillLightIntensity(intensity: number): void {
    this.fillLight.intensity = intensity
  }

  /**
   * 设置补光位置。
   *
   * @param x X 坐标。
   * @param y Y 坐标。
   * @param z Z 坐标。
   */
  public setFillLightPosition(x: number, y: number, z: number): void {
    this.fillLight.position.set(x, y, z)
  }

  /**
   * 设置反向补光强度。
   *
   * @param intensity 强度值。
   */
  public setBackLightIntensity(intensity: number): void {
    this.backLight.intensity = intensity
  }

  /**
   * 设置反向补光位置。
   *
   * @param x X 坐标。
   * @param y Y 坐标。
   * @param z Z 坐标。
   */
  public setBackLightPosition(x: number, y: number, z: number): void {
    this.backLight.position.set(x, y, z)
  }

  /**
   * 绑定相机控制器变化事件。
   *
   * 设计说明：
   * 1. 光照同步只依赖 CameraControls 的 `change` 事件，避免在每次渲染前重复更新灯光矩阵；
   * 2. CameraManager 切换投影类型会重建 controls，因此这里必须先解绑旧实例再绑定新实例；
   * 3. 绑定后立即同步一次，保证初始化和投影切换后的首帧光照方向正确。
   *
   * @param cameraControls 需要监听的相机控制器。
   */
  public bindCameraControls(cameraControls: CameraControls | null | undefined): void {
    if (this.cameraControls === cameraControls) {
      this.syncWithActiveCamera()
      return
    }

    this.cameraControls?.removeEventListener('change', this.handleCameraControlsChange)
    this.cameraControls = cameraControls ?? null
    this.cameraControls?.addEventListener('change', this.handleCameraControlsChange)
    this.syncWithActiveCamera()
  }

  /**
   * 按当前相机方向同步三平行光光组。
   *
   * 设计说明：
   * 1. DirectionalLight 不随距离衰减，因此这里使用相机前方的虚拟目标点稳定描述光照方向；
   * 2. 主光和补光位于相机侧，保证当前可见面不会因模型旋转到背面而变黑；
   * 3. 反向补光位于目标点后侧，用较低强度保留边缘层次，同时压低正反面亮度差。
   *
   * @param camera 当前渲染相机。
   */
  public syncWithCamera(camera: THREE.Camera): void {
    camera.updateMatrixWorld(true)
    this.cameraForward.setFromMatrixColumn(camera.matrixWorld, 2).negate().normalize()
    this.cameraRight.setFromMatrixColumn(camera.matrixWorld, 0).normalize()
    this.cameraUp.setFromMatrixColumn(camera.matrixWorld, 1).normalize()
    this.synchronizedLightTarget
      .copy(camera.position)
      .addScaledVector(this.cameraForward, this.cameraSynchronizedLightDistance)

    this.syncDirectionalLightWithCameraSpace(this.directionalLight, -0.28, 0.42, -1)
    this.syncDirectionalLightWithCameraSpace(this.fillLight, 0.76, 0.18, -0.85)
    this.syncDirectionalLightWithCameraSpace(this.backLight, -0.56, 0.26, 0.62)
  }

  /**
   * 释放光照资源。
   */
  public dispose(): void {
    this.cameraControls?.removeEventListener('change', this.handleCameraControlsChange)
    this.cameraControls = null
    this.scene.remove(this.ambientLight)
    this.disposeDirectionalLight(this.directionalLight)
    this.disposeDirectionalLight(this.fillLight)
    this.disposeDirectionalLight(this.backLight)
  }

  /**
   * 创建平行光并挂入场景。
   *
   * @param color 平行光颜色。
   * @param intensity 平行光强度。
   * @param position 初始平行光位置。
   * @returns 已挂入场景的平行光。
   */
  private createDirectionalLight(
    color: THREE.ColorRepresentation,
    intensity: number,
    position: [number, number, number],
  ): THREE.DirectionalLight {
    const light = new THREE.DirectionalLight(color, intensity)
    light.position.set(...position)
    light.target.position.set(0, 0, 0)
    light.target.updateMatrixWorld(true)
    addObject3DToParent(light, this.scene)
    return light
  }

  /**
   * 按 Viewer 当前活动相机同步灯光。
   */
  private syncWithActiveCamera(): void {
    const camera = this.viewer.camera
    if (!camera) {
      return
    }

    this.syncWithCamera(camera)
  }

  /**
   * 按相机局部坐标偏移同步单盏平行光。
   *
   * @param light 需要同步的平行光。
   * @param rightFactor 相机右向偏移系数。
   * @param upFactor 相机上向偏移系数。
   * @param forwardFactor 相机前向偏移系数。
   */
  private syncDirectionalLightWithCameraSpace(
    light: THREE.DirectionalLight,
    rightFactor: number,
    upFactor: number,
    forwardFactor: number,
  ): void {
    const distance = this.cameraSynchronizedLightDistance
    this.synchronizedLightPosition
      .copy(this.synchronizedLightTarget)
      .addScaledVector(this.cameraRight, rightFactor * distance)
      .addScaledVector(this.cameraUp, upFactor * distance)
      .addScaledVector(this.cameraForward, forwardFactor * distance)

    light.position.copy(this.synchronizedLightPosition)
    light.target.position.copy(this.synchronizedLightTarget)
    light.target.updateMatrixWorld(true)
    light.updateMatrixWorld(true)
  }

  /**
   * 从场景移除平行光并解除 target 引用的父子关系。
   *
   * @param light 待移除的平行光。
   */
  private disposeDirectionalLight(light: THREE.DirectionalLight): void {
    this.scene.remove(light)
    light.target.removeFromParent()
  }
}
